import type { ITableLikeTreeItem, ITableLikeTreeMenuOperationContext } from 'components/TableLikeTree/context';
import type { ITableLikeTreeFilterConfig } from 'components/TableLikeTree/TableLikeTree';
import { TableLikeTree } from 'components/TableLikeTree/TableLikeTree';
import type { TableColumn } from 'uikit';
import { TextField, Button } from 'uikit';

import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { tableLikeTreeLeftFilterMockItems, tableLikeTreeMockItems, tableLikeTreeTopFilterMockItems } from './mocks';

import Style from './TableLikeTree.module.css';

const additionalColumns: TableColumn<any>[] = [
    {
        title: 'Два в одном',
        columns: [
            {
                title: 'Колонка 1',
                accessor: 'column1',
                align: 'center',
                width: 100,
            },
            {
                title: 'Колонка 2',
                accessor: 'column2',
                align: 'center',
                width: 100,
            },
        ],
    },
];

const informer = [
    {
        treeItemId: 1,
        items: [{ message: 'Мега сообщение!' }],
    },
    {
        treeItemId: 5,
        items: [{ message: 'Гига сообщение!' }],
    },
];

export const TableLikeTreeTestPage: React.FC = () => {
    console.log('~ tableLikeTreeMockItems:', tableLikeTreeMockItems);
    const [nextId, setNextId] = useState(12);
    const [informers, setInformers] = useState(informer);
    const [itemToProcess, setItemToProcess] = useState<ITableLikeTreeItem | undefined>();
    const [parentToProcess, setParentToProcess] = useState<ITableLikeTreeItem | undefined>();
    const [title, setTitle] = useState<string | null>(null);
    const [processCallback, setProcessCallback] = useState<(item: ITableLikeTreeItem) => any | undefined>();
    const [addChildCallback, setAddChildCallback] =
        useState<(child: ITableLikeTreeItem, parent?: ITableLikeTreeItem) => any | undefined>();

    const onInformerClose = useCallback(
        (treeItemId: number) => {
            setInformers(informers.filter((i) => i.treeItemId !== treeItemId));
        },
        [informers],
    );

    const onInformersClose = useCallback(() => {
        setInformers([]);
    }, []);

    const onApply = useCallback(() => {
        if (itemToProcess) {
            itemToProcess.title = title || '';

            if (addChildCallback) {
                addChildCallback(itemToProcess, parentToProcess);
                setNextId(nextId + 1);
            } else {
                processCallback?.(itemToProcess);
            }

            setParentToProcess(undefined);
            setAddChildCallback(undefined);
            setProcessCallback(undefined);
            setItemToProcess(undefined);
            setTitle(null);
        }
    }, [addChildCallback, itemToProcess, nextId, parentToProcess, processCallback, title]);

    const onValueChange = useCallback(({ value }) => setTitle(value), []);

    const onAddChildExternallyCall = useCallback(
        (addChild: (child: ITableLikeTreeItem, parent?: ITableLikeTreeItem) => any, parent?: ITableLikeTreeItem) => {
            setParentToProcess(parent);
            setAddChildCallback(() => addChild);
            setItemToProcess({
                id: nextId,
                title: '',
            });
        },
        [nextId],
    );

    const menu = useMemo(
        () => [
            {
                label: 'Редактировать',
                action: (itemId?: number, context?: ITableLikeTreeMenuOperationContext) => {
                    setItemToProcess(context?.item);
                    setProcessCallback(() => context?.context?.replace);
                    setTitle(context?.item?.title || '');
                },
                itemId: 1,
                groupId: 1,
            },
            {
                label: 'Создать новый подобъект',
                itemId: 2,
                groupId: 1,
                action: (itemId?: number, context?: ITableLikeTreeMenuOperationContext) => {
                    setParentToProcess(context?.item);
                    setAddChildCallback(() => context?.context?.addChild);
                    setItemToProcess({
                        id: nextId,
                        title: '',
                    });
                },
            },
            {
                label: 'Удалить',
                action: (itemId?: number, context?: ITableLikeTreeMenuOperationContext) => {
                    context?.context?.delete(context?.item);
                },
                itemId: 3,
                groupId: 2,
            },
        ],
        [nextId],
    );

    const leftFilterConfig: ITableLikeTreeFilterConfig = useMemo(
        () => ({
            filterItems: tableLikeTreeLeftFilterMockItems,
            filteringFunction: (item: any, selectedIds: number[]) => selectedIds.includes(item.groupId),
        }),
        [],
    );

    const topFilterConfig: ITableLikeTreeFilterConfig = useMemo(
        () => ({
            filterItems: tableLikeTreeTopFilterMockItems,
            filteringFunction: (item: any, selectedIds: number[]) => selectedIds.includes(item.year),
            title: 'Верхний фильтр',
        }),
        [],
    );

    return (
        <div className={Style.tableLikeTreeContainer}>
            {itemToProcess && (
                <div className={Style.editForm}>
                    <TextField value={title} onChange={onValueChange} size="s" />
                    <Button onClick={onApply} title="ok" size="s" label="ok" />
                </div>
            )}
            <TableLikeTree
                hierarchyColumnTitle="Объекты иерархии"
                items={tableLikeTreeMockItems}
                menuItems={menu}
                isRowCheckAvailable
                showExpandAllSwitchInHierarchyHeader
                className={Style.tableLikeTree}
                setContextMenuInHierarchyColumn
                additionalColumns={additionalColumns}
                informers={informers}
                onInformerClose={onInformerClose}
                onInformersClose={onInformersClose}
                showClearNotifications
                onAddChildExternallyCall={onAddChildExternallyCall}
                showCreateNewObjectButton
                leftFilterConfig={leftFilterConfig}
                topFilterConfig={topFilterConfig}
            />
        </div>
    );
};

TableLikeTreeTestPage.displayName = 'TableLikeTreeTestPage';
